import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TicketPriority, TicketPriorityFormData } from '@/types/ticketPriority';
import { ticketStatusService } from '../ticketStatuses/ticketStatusService';

interface TicketPrioritiesState {
  priorities: TicketPriority[];
  loading: boolean;
  error: string | null;
  isFormOpen: boolean;
  editingPriority: TicketPriority | null;
  isSubmitting: boolean;
}

const initialPriorities: TicketPriority[] = [
  {
    id: '1',
    name: 'Low',
    color: '#10B981',
    emoji: '🔵',
    description: 'Low priority tickets that can be addressed when time permits',
    isDefault: true,
    isSystemPriority: true,
    order: 1,
  },
  {
    id: '2',
    name: 'Medium',
    color: '#F59E0B',
    emoji: '🟡',
    description: 'Medium priority tickets requiring timely attention',
    isSystemPriority: true,
    order: 2,
  },
  {
    id: '3',
    name: 'High',
    color: '#EF4444',
    emoji: '🔴',
    description: 'High priority tickets requiring immediate attention',
    isSystemPriority: true,
    order: 3,
  },
  {
    id: '4',
    name: 'Critical',
    color: '#DC2626',
    emoji: '🚨',
    description: 'Critical priority requiring immediate resolution',
    isSystemPriority: false,
    order: 4,
  },
  {
    id: '5',
    name: 'Urgent',
    color: '#7C3AED',
    emoji: '⚡',
    description: 'Urgent priority for time-sensitive issues',
    isSystemPriority: true,
    order: 5,
  },
];

const initialState: TicketPrioritiesState = {
  priorities: [],
  loading: false,
  error: null,
  isFormOpen: false,
  editingPriority: null,
  isSubmitting: false,
};

export const createTicketPriority = createAsyncThunk(
  'ticketPriorities/create',
  async (data: TicketPriorityFormData) => {
    const response = await ticketStatusService.createTicketPriority(data);
    return response;
  }
);

export const updateTicketPriority = createAsyncThunk(
  'ticketPriorities/update',
  async ({ id, data }: { id: string; data: TicketPriorityFormData }) => {
    const response = await ticketStatusService.updateTicketPriority(id, data);
    return response;
  }
);

export const deleteTicketPriority = createAsyncThunk(
  'ticketPriorities/delete',
  async (id: string) => {
    const response = await ticketStatusService.deleteTicketPriority(id);
    return response; // Now returns the full list of priorities
  }
);

export const reorderTicketPriorities = createAsyncThunk(
  'ticketPriorities/reorder',
  async (priorities: TicketPriority[]) => {
    // Update order for each priority in the API
    await Promise.all(
      priorities.map(async (priority) => {
        await ticketStatusService.updateTicketPriority(priority.id, {
          name: priority.name,
          description: priority.description,
          color: priority.color,
          emoji: priority.emoji,
          isDefault: priority.isDefault,
          order: priority.order,
        });
      })
    );
    return priorities;
  }
);

// New async function that updates API without waiting for component
export const updatePriorityOrderInAPI = createAsyncThunk(
  'ticketPriorities/updateOrderAPI',
  async (priorities: TicketPriority[]) => {
    // Update order for each priority in the API
    await Promise.all(
      priorities.map(async (priority) => {
        await ticketStatusService.updateTicketPriority(priority.id, {
          order: priority.order,
        });
      })
    );
    return priorities;
  }
);

export const fetchTicketPriorities = createAsyncThunk(
  'ticketPriorities/fetchTicketPriorities',
  async () => {
    const response = await ticketStatusService.listTicketPriorities();
    return response;
  }
);

const ticketPrioritiesSlice = createSlice({
  name: 'ticketPriorities',
  initialState,
  reducers: {
    openForm: (state) => {
      state.isFormOpen = true;
      state.editingPriority = null;
    },
    openEditForm: (state, action: PayloadAction<TicketPriority>) => {
      state.isFormOpen = true;
      state.editingPriority = action.payload;
    },
    closeForm: (state) => {
      state.isFormOpen = false;
      state.editingPriority = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    updatePrioritiesOptimistic: (state, action: PayloadAction<TicketPriority[]>) => {
      state.priorities = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTicketPriority.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(createTicketPriority.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.priorities = action.payload; // Replace with full list
        state.isFormOpen = false;
      })
      .addCase(createTicketPriority.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'Failed to create priority';
      })
      .addCase(updateTicketPriority.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(updateTicketPriority.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.priorities = action.payload; // Replace with full list
        state.isFormOpen = false;
        state.editingPriority = null;
      })
      .addCase(updateTicketPriority.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'Failed to update priority';
      })
      .addCase(deleteTicketPriority.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTicketPriority.fulfilled, (state, action) => {
        state.loading = false;
        state.priorities = action.payload; // Replace with full list
      })
      .addCase(deleteTicketPriority.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete priority';
      })
      .addCase(reorderTicketPriorities.pending, (state) => {
        // Optionally set a reordering flag if you want to show loading state
        state.error = null;
      })
      .addCase(reorderTicketPriorities.fulfilled, (state, action) => {
        state.priorities = action.payload;
      })
      .addCase(reorderTicketPriorities.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update priority order';
        // Note: The UI will need to refetch priorities to revert the order on error
      })
      .addCase(updatePriorityOrderInAPI.pending, (state) => {
        // Don't change loading state for background API updates
        state.error = null;
      })
      .addCase(updatePriorityOrderInAPI.fulfilled, (state) => {
        // Success - no UI update needed since we already updated optimistically
      })
      .addCase(updatePriorityOrderInAPI.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update priority order';
        // The UI component will handle reverting the optimistic update
      })
      .addCase(fetchTicketPriorities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketPriorities.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both wrapped ({data: [...]} ) and direct array responses
        state.priorities = Array.isArray(action.payload) ? action.payload : action.payload.data;
      })
      .addCase(fetchTicketPriorities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch priorities';
      })
  },
});

export const { openForm, openEditForm, closeForm, clearError, updatePrioritiesOptimistic } = ticketPrioritiesSlice.actions;
export const ticketPrioritiesReducer = ticketPrioritiesSlice.reducer;

// Selectors
export const selectTicketPriorities = (state: { ticketPriorities: TicketPrioritiesState }) => state.ticketPriorities.priorities;
export const selectTicketPrioritiesLoading = (state: { ticketPriorities: TicketPrioritiesState }) => state.ticketPriorities.loading;
export const selectTicketPrioritiesError = (state: { ticketPriorities: TicketPrioritiesState }) => state.ticketPriorities.error;
export const selectIsPriorityFormOpen = (state: { ticketPriorities: TicketPrioritiesState }) => state.ticketPriorities.isFormOpen;
export const selectEditingPriority = (state: { ticketPriorities: TicketPrioritiesState }) => state.ticketPriorities.editingPriority;
export const selectIsPrioritySubmitting = (state: { ticketPriorities: TicketPrioritiesState }) => state.ticketPriorities.isSubmitting;
